import { jsxs, jsx } from 'react/jsx-runtime';
import { Conversation, ConversationContent, ConversationScrollButton } from '../../ai-elements/conversation.js';
import { memo, useMemo, useCallback } from 'react';
import { useConversationScrollDetection } from '../hooks/useConversationScrollDetection.js';
import ConciergeWelcome from './ConciergeWelcome.js';
import { ConversationSkeleton } from './ConversationSkeleton.js';
import { IconHome } from './ConciergeIcons.js';
import { MessageListV2 } from './MessageListV2.js';
import { PromptInputConcierge } from './PromptInputConcierge.js';
import { ConciergeTrendingSearches } from './ConciergeTrendingSearches.js';
import { useSuggestedQueries } from '../../api/cache/appConfigQueryOptions.js';

// Separate input component that can re-render without affecting conversation area
const ConciergeInputArea = ({ searchInputValue, setSearchInputValue, handleSearch, isActivelyStreaming, conversationHistoryLength, onTrackAccessPoint, }) => {
    const placeholder = useMemo(() => (conversationHistoryLength > 0 ? 'Ask a follow up question' : 'How can I help you today?'), [conversationHistoryLength]);
    // Wrap handleSearch to track the access point
    const handleSearchWithTracking = useCallback((text, imageData, imageMimeType) => {
        // Track search_bar access point (non-blocking)
        onTrackAccessPoint === null || onTrackAccessPoint === void 0 ? void 0 : onTrackAccessPoint('search_bar', text);
        handleSearch(text, imageData, imageMimeType);
    }, [handleSearch, onTrackAccessPoint]);
    return (jsx("div", Object.assign({ className: "tw:flex tw:shrink-0 tw:flex-col tw:gap-4 tw:px-4 tw:pt-4", style: { paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' } }, { children: jsx(PromptInputConcierge, { placeholder: placeholder, value: searchInputValue, onChange: setSearchInputValue, onSubmit: handleSearchWithTracking, isStreaming: isActivelyStreaming }) })));
};
const ConciergeChatAreaV2Component = ({ appName: _appName, brandId, conversationId, isEmbedded = false, apiBaseUrl, userFirstName, pageContext, conversationHistory, productMetadataMap, surveyMetadataMap, streamingProductGroups, previewSearchQueries, previewQueryProducts, followUpQuestions, streamingFeaturedProduct, streamingStoreLocator, isActivelyStreaming, streamingAIResponse, currentStatus, isWaitingForFollowUpQueries, isLoadingConversationQuery, handleSearch, onUpdateFeedback, onVariantSelect, onTrackAccessPoint, onHomeClick, isSidebarExpanded = true, brandLogoUrl, searchInputValue, setSearchInputValue, isSharedView, sharedByUserName, sourceConversationId, sourceCheckpointIndex, }) => {
    // Derived state
    const showWelcome = conversationHistory.length === 0 && !isLoadingConversationQuery;
    // Fetch suggested queries for the carousel
    const { data: suggestedQueriesData } = useSuggestedQueries(brandId ? parseInt(brandId) : undefined, apiBaseUrl);
    const suggestedQueries = useMemo(() => (suggestedQueriesData === null || suggestedQueriesData === void 0 ? void 0 : suggestedQueriesData.suggestedQuestions) || [], [suggestedQueriesData]);
    // Use simple header + carousel layout on full page (!isEmbedded)
    // Use text suggestions layout on embedded/window mode (isEmbedded)
    const hasImageCarousel = !isEmbedded;
    // Show typing indicator only when actively streaming but no search queries/products AND no text content yet
    const showTypingIndicator = isActivelyStreaming &&
        !streamingAIResponse &&
        previewSearchQueries.length === 0 &&
        previewQueryProducts.length === 0;
    // Scroll-based suggestions animation
    // Memoize the hook call to prevent re-renders when searchInputValue changes
    const scrollDetectionParams = useMemo(() => ({
        followUpQuestions,
        isActivelyStreaming,
        conversationHistoryLength: conversationHistory.length,
        showWelcome,
        searchInputValue: '', // Not actually used in the hook, but required by interface
    }), [followUpQuestions, isActivelyStreaming, conversationHistory.length, showWelcome]);
    const { bottomRef } = useConversationScrollDetection(scrollDetectionParams);
    // Memoize handleSearch callback to prevent welcome component re-renders
    const stableHandleSearch = useMemo(() => handleSearch, [handleSearch]);
    // Create wrapped handlers that track access points
    const handleWelcomeSearch = useCallback((searchTerm) => {
        // Track quick_prompts access point (non-blocking)
        onTrackAccessPoint === null || onTrackAccessPoint === void 0 ? void 0 : onTrackAccessPoint('quick_prompts', searchTerm);
        stableHandleSearch(searchTerm);
    }, [stableHandleSearch, onTrackAccessPoint]);
    const handleFollowUpSearch = useCallback((searchTerm) => {
        // Track follow_up_question access point (non-blocking)
        onTrackAccessPoint === null || onTrackAccessPoint === void 0 ? void 0 : onTrackAccessPoint('follow_up_question', searchTerm);
        stableHandleSearch(searchTerm);
    }, [stableHandleSearch, onTrackAccessPoint]);
    // Memoize welcome component props to prevent re-renders
    const welcomeProps = useMemo(() => ({
        onSearch: handleWelcomeSearch,
        brandId: brandId ? parseInt(brandId) : undefined,
        isEmbedded,
        apiBaseUrl,
        userFirstName,
        pageContext,
    }), [handleWelcomeSearch, brandId, isEmbedded, apiBaseUrl, userFirstName, pageContext]);
    // Simple header for image carousel layout (CONCIERGE + greeting)
    const SimpleWelcomeHeader = useMemo(() => (jsxs("div", Object.assign({ className: "tw:flex tw:flex-col tw:items-center tw:justify-center tw:w-full tw:text-center" }, { children: [jsx("p", Object.assign({ className: "tw:font-bold tw:text-[16px] tw:leading-[32px] tw:tracking-[2px] tw:uppercase tw:text-primary" }, { children: "CONCIERGE" })), jsxs("h1", Object.assign({ className: "tw:font-semibold tw:text-[24px] tw:leading-[32px] tw:tracking-[0.08px] tw:mt-4 tw:text-primary" }, { children: ["Let us help you find what you've been looking for", userFirstName ? `, ${userFirstName}` : '', "."] }))] }))), [userFirstName]);
    // Memoize the conversation content to prevent re-renders when searchInputValue changes
    const conversationContent = useMemo(() => {
        if (isLoadingConversationQuery) {
            return jsx(ConversationSkeleton, {});
        }
        if (showWelcome) {
            // Use simple header when showing image carousel, full welcome otherwise
            if (hasImageCarousel) {
                return SimpleWelcomeHeader;
            }
            return jsx(ConciergeWelcome, Object.assign({}, welcomeProps));
        }
        return (jsx(MessageListV2, { conversationId: conversationId, messages: conversationHistory, showTypingIndicator: showTypingIndicator, streamingAIResponse: streamingAIResponse, productMetadataMap: productMetadataMap, surveyMetadataMap: surveyMetadataMap, streamingProductGroups: streamingProductGroups, currentStatus: currentStatus, previewSearchQueries: previewSearchQueries, previewQueryProducts: previewQueryProducts, isWaitingForFollowUpQueries: isWaitingForFollowUpQueries, isActivelyStreaming: isActivelyStreaming, streamingFeaturedProduct: streamingFeaturedProduct, streamingStoreLocator: streamingStoreLocator, followUpQuestions: followUpQuestions, onSearch: handleFollowUpSearch, onUpdateFeedback: onUpdateFeedback, onVariantSelect: onVariantSelect, bottomRef: bottomRef, apiBaseUrl: apiBaseUrl, isSharedView: isSharedView, sharedByUserName: sharedByUserName, sourceConversationId: sourceConversationId, sourceCheckpointIndex: sourceCheckpointIndex }));
    }, [
        isLoadingConversationQuery,
        showWelcome,
        hasImageCarousel,
        SimpleWelcomeHeader,
        welcomeProps,
        conversationId,
        conversationHistory,
        showTypingIndicator,
        streamingAIResponse,
        productMetadataMap,
        streamingProductGroups,
        currentStatus,
        previewSearchQueries,
        previewQueryProducts,
        isWaitingForFollowUpQueries,
        isActivelyStreaming,
        streamingFeaturedProduct,
        streamingStoreLocator,
        followUpQuestions,
        handleFollowUpSearch,
        onUpdateFeedback,
        onVariantSelect,
        bottomRef,
        isSharedView,
        sharedByUserName,
        sourceConversationId,
        sourceCheckpointIndex,
    ]);
    return (jsxs("div", Object.assign({ className: "tw:relative tw:flex tw:flex-1 tw:flex-col", style: {
            minHeight: 0,
            maxHeight: '100%',
            overflow: 'hidden',
        } }, { children: [onHomeClick && !isSidebarExpanded && (jsx("div", Object.assign({ className: "tw:flex tw:items-center tw:shrink-0 tw:px-4 tw:py-3", style: { minHeight: 48 } }, { children: brandLogoUrl ? (jsx("button", Object.assign({ type: "button", onClick: onHomeClick, className: "tw:flex tw:items-center tw:rounded tw:p-1 tw:transition-colors hover:tw:bg-black/5", "aria-label": "Go to home" }, { children: jsx("img", { src: brandLogoUrl, alt: "Brand", className: "tw:h-10 tw:w-auto tw:max-w-[140px] tw:object-contain" }) }))) : (jsxs("button", Object.assign({ type: "button", onClick: onHomeClick, className: "tw:flex tw:items-center tw:gap-2 tw:rounded tw:px-2 tw:py-1.5 tw:text-sm tw:font-medium tw:text-primary hover:tw:bg-black/5 tw:transition-colors", "aria-label": "Go to home" }, { children: [jsx(IconHome, { size: 20 }), jsx("span", { children: "Home" })] }))) }))), jsx("div", Object.assign({ style: {
                    flex: 1,
                    overflow: showWelcome ? 'auto' : 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    maxHeight: '100%',
                } }, { children: showWelcome ? (
                // Welcome screen layout - different based on whether we have image carousel
                hasImageCarousel ? (
                // Image carousel layout: header -> input -> carousel
                jsxs("div", Object.assign({ style: {
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                        overflow: 'auto',
                    } }, { children: [jsx("div", { style: { height: '120px', flexShrink: 0 } }), conversationContent, jsx("div", Object.assign({ className: "tw:mt-8 tw:w-full tw:max-w-[768px] tw:px-4 md:tw:px-0" }, { children: jsx(PromptInputConcierge, { placeholder: "Ask anything", value: searchInputValue, onChange: setSearchInputValue, onSubmit: handleSearch, isStreaming: isActivelyStreaming }) })), jsx(ConciergeTrendingSearches, { suggestedQueries: suggestedQueries, onSearch: handleSearch }), jsx("div", { style: { height: '40px', flexShrink: 0 } })] }))) : (
                // Simple centered layout for non-image mode
                jsx("div", Object.assign({ style: {
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    } }, { children: conversationContent })))) : (jsxs(Conversation, { children: [jsx(ConversationContent, Object.assign({ className: "tw:flex tw:items-center tw:justify-center" }, { children: conversationContent })), jsx(ConversationScrollButton, {})] })) })), (!showWelcome || !hasImageCarousel) && (jsx(ConciergeInputArea, { searchInputValue: searchInputValue, setSearchInputValue: setSearchInputValue, handleSearch: handleSearch, isActivelyStreaming: isActivelyStreaming, conversationHistoryLength: conversationHistory.length, onTrackAccessPoint: onTrackAccessPoint }))] })));
};
// Memoize the chat area - input is separated so it can re-render without affecting conversation
const ConciergeChatAreaV2 = memo(ConciergeChatAreaV2Component);

export { ConciergeChatAreaV2 };
//# sourceMappingURL=ConciergeChatAreaV2.js.map
